import React, { useCallback, useEffect, useRef, useState } from "react";
import { useContext } from "react";
import { useLanguage } from "../../../../hooks/use-language";
import { useTranslations } from "../../../../hooks/use-translations";
import { useLocation } from "react-router-dom";
import { store } from "../../../Store";
import { HGDropdownResources } from "./hg-dropdown-resources";

const ACTIVE_PATHS = [
    "/comunicacion",
    "/centro-pedagogico",
    "/blog",
    "/ayuda",
    "/ayuda-sin-login",
];

/**
 * Renderiza el botón del menú desplegable de Recursos del Header General y publico.
 *
 * El componente administra el estado de apertura del menú de Recursos y
 * muestra las opciones disponibles mediante el componente
 * `HGDropdownResources`. También controla el cierre automático del menú al
 * detectar clics fuera del componente o desplazamientos de la página, y marca
 * el botón como activo cuando la ruta actual corresponde a una sección de
 * recursos.
 * @returns {JSX.Element} HGResourcesButton
 */
export const HGResourcesButton = () => {
    const { state } = useContext(store);
    const { pathname } = useLocation();
    const lang = useLanguage();
    const textosInterfaz = useTranslations({ section: "header", lang: lang, array: true });

    const recursosRef = useRef(null);
    const [openDropdownResources, setOpenDropdownResources] = useState(false);
    const isAuthenticated = state?.sesionActiva === "SI";

    const closeDropdown = useCallback(() => {
        setOpenDropdownResources(false);
    }, []);

    const toggleDropdown = () => {
        setOpenDropdownResources((prev) => !prev);
    };

    const handleOutsideClick = (event) => {
        if (recursosRef.current && !recursosRef.current.contains(event.target)) {
            setOpenDropdownResources(false)
        }
    };

    /**Función que permite cerrar el dropdown Comunidad dando click fuera de él*/
    useEffect(() => {
        document.addEventListener('click', handleOutsideClick);

        return () => {
            document.removeEventListener('click', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 0) {
                closeDropdown();
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [closeDropdown]);

    const isActive = ACTIVE_PATHS.includes(pathname);

    return (
        <div
            ref={recursosRef}
            className={`ml-3 d-flex align-items-center relative h-100`}
        >
            <button
                className={`
                    focusable-red
                    d-flex
                    justify-content-center
                    align-items-center
                    body-m
                    btn-header-general
                    ${isActive ? "activeMode" : ""}
                    ${openDropdownResources ? "openMode" : ""}
                `}
                onClick={toggleDropdown}
                aria-expanded={openDropdownResources}
                aria-haspopup="menu"
                aria-label={
                    textosInterfaz?.button_resources_description ??
                    "Abrir la lista desplegable de páginas de recursos"
                }
            >
                {textosInterfaz?.recursos2 || 'Recursos'}
                <i
                    className={`fas fa-chevron-down fa-sm ml-2 arrow d-none d-lg-block ${openDropdownResources && "active-arrow"}`}
                    aria-hidden={true}
                />
            </button>

            <div className={`dropdownMenu ${openDropdownResources ? 'activeMode' : 'inactiveMode'} `}>
                <HGDropdownResources
                    setOpenDropdown={setOpenDropdownResources}
                    openDropdown={openDropdownResources}
                    textosInterfaz={textosInterfaz}
                    isAuthenticated={isAuthenticated}
                />
            </div>
        </div>
    )
}